/*
  Offers a companion product after a trigger product is added to the cart.

  snippets/capra-cart-upsell.liquid renders the modal with the trigger and
  companion ids from the Cart prompts theme settings. This element listens
  for product-form.js's cart-update event (cartData is the /cart/add.js
  response: one line item, or { items } for multi-line forms), and when the
  trigger product is among the added lines it fetches the cart to make sure
  the companion is not already there, then opens with the stepper reset to
  its minimum. Accepting posts the companion at the chosen quantity to
  /cart/add.js with the drawer's section ids so the popover
  and header count re-render, and publishes cart-update with source
  'capra-upsell' for Dawn's other subscribers. Declining is remembered for
  the session so the shopper is not asked again on every add.
*/
class CapraCartUpsell extends HTMLElement {
  static HIDE_DELAY = 220;
  static STORAGE_KEY = 'capra-upsell-declined';

  constructor() {
    super();
    this.dialog = this.querySelector('[role="dialog"]');
    this.acceptButton = this.querySelector('[data-upsell-accept]');
    this.acceptLabel = this.querySelector('.capra-upsell__accept-label');
    this.priceElement = this.querySelector('[data-upsell-price]');
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.unitPriceText = this.priceElement?.textContent.trim() || '';
    this.errorElement = this.querySelector('[data-upsell-error]');
    this.drawer = document.querySelector('cart-drawer');
    this.triggerProductId = Number(this.dataset.triggerProductId);
    this.triggerVariantIds = (this.dataset.triggerVariantIds || '')
      .split(',')
      .map(Number)
      .filter(Boolean);
    this.productId = Number(this.dataset.productId);
    this.variantId = Number(this.dataset.variantId);
    this.busy = false;
    this.opener = null;
  }

  connectedCallback() {
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));

    this.querySelectorAll('[data-upsell-dismiss]').forEach((element) => {
      element.addEventListener('click', () => this.decline());
    });
    this.acceptButton.addEventListener('click', () => this.accept());
    this.addEventListener('keydown', (event) => {
      if (event.code === 'Escape') this.decline();
    });
    this.quantityInput?.addEventListener('change', () => this.renderPrice());
  }

  get quantity() {
    const value = parseInt(this.quantityInput?.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  renderPrice() {
    if (!this.priceElement) return;
    const unit = parseInt(this.acceptButton.dataset.unitPrice, 10);
    const format = this.acceptButton.dataset.moneyFormat;
    const quantity = this.quantity;
    if (quantity === 1 || !Number.isFinite(unit) || !format || typeof window.capraFormatMoney !== 'function') {
      this.priceElement.textContent = this.unitPriceText;
      return;
    }
    this.priceElement.textContent = window.capraFormatMoney(unit * quantity, format);
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  onCartUpdate(event) {
    if (!event || event.source !== 'product-form') return;
    if (!this.triggerWasAdded(event)) return;
    if (this.declinedThisSession()) return;
    this.companionInCart().then((inCart) => {
      if (!inCart) this.show();
    });
  }

  triggerWasAdded(event) {
    const data = event.cartData;
    const items = Array.isArray(data?.items) ? data.items : data ? [data] : [];
    if (items.some((item) => Number(item.product_id) === this.triggerProductId)) return true;
    return this.triggerVariantIds.includes(Number(event.productVariantId));
  }

  companionInCart() {
    return fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((response) => response.json())
      .then((cart) => (cart.items || []).some((item) => Number(item.product_id) === this.productId))
      .catch(() => false);
  }

  show() {
    if (!this.hidden) return;
    this.opener = document.activeElement;
    this.setError('');
    if (this.quantityInput) {
      this.quantityInput.value = this.quantityInput.min || 1;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.hidden = false;
    // Two frames so the transition runs from the hidden state.
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
    document.body.classList.add('overflow-hidden');
    trapFocus(this, this.dialog);
  }

  hide() {
    if (this.hidden) return;
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.opener);
    setTimeout(() => {
      if (this.classList.contains('is-open')) return;
      this.hidden = true;
    }, CapraCartUpsell.HIDE_DELAY);
  }

  decline() {
    if (this.busy) return;
    this.rememberDecline();
    this.hide();
  }

  accept() {
    if (this.busy) return;
    this.setBusy(true);
    this.setError('');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', this.variantId);
    formData.append('quantity', this.quantity);
    if (this.drawer) {
      formData.append('sections', this.drawer.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;

    fetch(window.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.setError(response.description || response.message || window.cartStrings?.error);
          return;
        }
        if (this.drawer) {
          this.drawer.classList.remove('is-empty');
          this.drawer.renderContents(response);
        }
        this.hide();
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'capra-upsell',
          productVariantId: this.variantId,
          cartData: response,
        });
      })
      .catch(() => {
        this.setError(window.cartStrings?.error || 'Something went wrong. Please try again.');
      })
      .finally(() => this.setBusy(false));
  }

  setBusy(busy) {
    this.busy = busy;
    this.acceptButton.classList.toggle('is-loading', busy);
    this.acceptButton.setAttribute('aria-disabled', String(busy));
    this.acceptLabel.textContent = busy ? 'Adding' : this.acceptButton.dataset.label;
  }

  setError(message) {
    this.errorElement.textContent = message || '';
    this.errorElement.hidden = !message;
  }

  declinedThisSession() {
    try {
      return sessionStorage.getItem(CapraCartUpsell.STORAGE_KEY) === String(this.productId);
    } catch (error) {
      return false;
    }
  }

  rememberDecline() {
    try {
      sessionStorage.setItem(CapraCartUpsell.STORAGE_KEY, String(this.productId));
    } catch (error) {
      // Storage can be unavailable in private windows; asking again is fine.
    }
  }
}

customElements.define('capra-cart-upsell', CapraCartUpsell);
